import { memo } from 'react'
import cn from 'clsx'

// Components
import { Button, IconDownloadCircle, IconDownloadCircleFilled, Tooltip } from '@tracklib/kit'

// Hooks
import { useProfile } from 'modules/profile/hooks'
import { useRequest } from 'modules/requests/hooks'
import { useActions } from 'modules/audio/hooks'
import { useIsFreeTrack } from 'modules/price/hooks'
import { useTrackIsUnpublished } from 'modules/entities/hooks'

// Types
import type { Loop } from 'types'

import styles from './downloadbutton.css'

type TrackDownloadButtonProps = {
  trackId: number
  loop?: Loop | null | undefined
  className?: string
  expanded?: boolean
  extraCompact?: boolean
}

const TrackDownloadButton = ({ trackId, loop, className, expanded, extraCompact }: TrackDownloadButtonProps): JSX.Element => {
  const profile = useProfile()
  const isUnpublished = useTrackIsUnpublished(trackId)
  const { handleDownload } = useActions()
  const request = useRequest('FETCH_WEBSOCKET_LINK', `${trackId}${loop ? `-${loop.beat_start}:${loop.beat_count}` : ''}`)
  const isOwned = !!profile.purchased_tracks.find(x => +x.track === +trackId)
  const isOwnedTooltip = `Download ${loop ? 'loop' : 'track'}`
  const isFreeTrack = useIsFreeTrack(trackId)
  const price = isFreeTrack ? 'for free' : '(1 credit)'
  const isNotOwnedTooltip = isUnpublished ? (
    <span>Unpublished</span>
  ) : (
    <span>
      Download {loop ? 'loop' : 'track'} <span className={styles.creditCost}>{price}</span>
    </span>
  )
  return (
    <div data-tooltip-wrapper className={className}>
      {!(isFreeTrack && !expanded) && <Tooltip className={styles.tooltip}>{isOwned ? isOwnedTooltip : isNotOwnedTooltip}</Tooltip>}
      <Button
        datatesting={isOwned ? 'track_download_button' : 'track_purchase_button'}
        title={loop ? 'Download loop' : 'Download track'}
        onClick={() => handleDownload(trackId, loop)}
        className={cn(styles.button, isOwned && styles.owned, expanded && styles.expanded, isFreeTrack && !isOwned && styles.isFreeTrack)}
        disabled={request?.isLoading || isUnpublished}
        isLoading={request?.isLoading}
        spinnerBorderWidth={2}
        spinnerColored
      >
        {expanded ? (
          <IconDownloadCircle className={styles.iconExpanded} />
        ) : (
          <> {isOwned ? <IconDownloadCircle /> : <IconDownloadCircleFilled />}</>
        )}
        {expanded && <>{isFreeTrack ? 'Free download' : 'Download'}</>}
        {isFreeTrack && !expanded && !isOwned && <span className={styles.buttonTooltip}>Free{!extraCompact && ' download'}</span>}
      </Button>
    </div>
  )
}

export { TrackDownloadButton as Component }
export default memo<TrackDownloadButtonProps>(TrackDownloadButton)
